import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Badge, Table, Spinner, Alert, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import BreadCrumb from "../../../components/common/BreadCrumb";
import { listCampaigns } from "../../../slices/veloxity/pushNotification/listCampaigns";
import { enableCampaign, disableCampaign, deleteCampaign, clearCampaignActionState } from "../../../slices/veloxity/pushNotification/campaignActions";
import { FaEye, FaPause, FaPlay, FaPlus, FaTrash } from "react-icons/fa";
import showConfirmationAlert from "../../../components/custom/confirmationAlert";

const CampaignManagement = () => {
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    const { campaigns, loading, error } = useSelector((state) => state.listCampaigns);
    const campaignActions = useSelector((state) => state.campaignActions);
    
    const [displayCampaigns, setDisplayCampaigns] = useState([]);
    const [actionMessage, setActionMessage] = useState(null);
    
    useEffect(() => {
        dispatch(listCampaigns());
    }, [dispatch]);
    
    useEffect(() => {
        if (campaigns && campaigns.length > 0) {
            setDisplayCampaigns(formatCampaignData(campaigns));
        } else if (error) {
            setDisplayCampaigns(formatCampaignData([]));
        } else if (!loading && (!campaigns || campaigns.length === 0)) {
            setDisplayCampaigns(formatCampaignData([]));
        }
    }, [campaigns, error, loading]);

    useEffect(() => {
        if (campaignActions.success) {
            dispatch(listCampaigns());
            
            let message = "";
            switch (campaignActions.actionType) {
                case "enable":
                    message = "Campaign successfully enabled";
                    break;
                case "disable":
                    message = "Campaign successfully disabled";
                    break;
                case "delete":
                    message = "Campaign successfully deleted";
                    break;
                default:
                    message = "Operation completed successfully";
            }
            
            setActionMessage({ type: "success", text: message });
            
            setTimeout(() => {
                dispatch(clearCampaignActionState());
                setActionMessage(null);
            }, 3000);
        }
        
        if (campaignActions.error) {
            setActionMessage({ 
                type: "danger", 
                text: `Error: ${campaignActions.error?.message || "Operation failed"}` 
            });
            
            setTimeout(() => {
                dispatch(clearCampaignActionState());
                setActionMessage(null);
            }, 5000);
        }
    }, [campaignActions, dispatch]);

    const formatCampaignData = (apiData) => {
        return apiData.map(campaign => ({
            id: campaign.id,
            name: campaign.name,
            description: campaign.description,
            status: campaign.campaignStatus,
            eventType: campaign.eventType,
            audienceId: campaign.audienceId,
            audienceCount: campaign.audienceCount,
            dailyQuota: campaign.dailyQuota,
            totalQuota: campaign.totalQuota,
            startDate: campaign.startDateTime,
            endDate: campaign.endDateTime,
            timeRanges: campaign.timeRanges,
            offer: campaign.offer,
            eventConfig: campaign.eventConfig,
            metrics: { 
                impressions: Math.floor(Math.random() * 10000),
                clicks: Math.floor(Math.random() * 2000),
                conversions: Math.floor(Math.random() * 500)
            }
        }));
    };


    const getCampaignType = (eventType) => {
        switch(eventType) {
            case "IN_APP_EVENT":
                return "In-App";
            case "REAL_TIME_EVENT":
                return "Real-Time";
            default:
                return eventType;
        }
    };

    const getAudienceInfo = (campaign) => {
        if (campaign.audienceId) {
            return <Badge color="primary">Predefined Audience</Badge>;
        } else if (campaign.eventType === "REAL_TIME_EVENT" && campaign.eventConfig) {
            return <Badge color="warning">Dynamic Audience</Badge>;
        }
        
        return <Badge color="light">Not Defined</Badge>;
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case "IN_PROGRESS":
                return <Badge color="success">Active</Badge>;
            case "DISABLED":
                return <Badge color="warning">Disabled</Badge>;
            case "AUDIENCE_PROCESSING":
                return <Badge color="info">Processing Audience</Badge>;
            case "AUDIENCE_FAILED":
                return <Badge color="danger">Audience Failed</Badge>;
            case "QUOTA_COMPLETED":
                return <Badge color="secondary">Quota Completed</Badge>;
            case "EXPIRED":
                return <Badge color="dark">Expired</Badge>;
            default:
                return <Badge color="light">Unknown</Badge>;
        }
    };

    const getNotificationType = (offer) => {
        if (!offer) return <Badge color="light">Unknown</Badge>;
        
        switch(offer.type) {
            case "SMART_PN":
                return <Badge color="info" className="bg-gradient">Smart Notification</Badge>;
            case "RICH_NOTIFICATION":
                return <Badge color="purple" style={{ backgroundColor: '#8e44ad', color: 'white' }}>Rich Notification</Badge>;
            default:
                return <Badge color="secondary">{offer.type}</Badge>;
        }
    };

    const handleStatusChange = (campaignId, currentStatus) => {
        if (currentStatus === "DISABLED") {
            showConfirmationAlert(
                "Are you sure you want to activate this campaign?",
                () => dispatch(enableCampaign(campaignId))
            );
        } else if (currentStatus === "IN_PROGRESS") {
            showConfirmationAlert(
                "Are you sure you want to disable this campaign?",
                () => dispatch(disableCampaign(campaignId))
            );
        }
    };

    const handleDeleteCampaign = (campaignId, campaignName) => {
        showConfirmationAlert(
            `Are you sure you want to delete the campaign "${campaignName}"? This action cannot be undone.`,
            () => dispatch(deleteCampaign(campaignId))
        );
    };

    const filteredCampaigns = displayCampaigns.filter(
        campaign => campaign.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const toggleModal = () => {
        setIsModalOpen(!isModalOpen);
        if (isModalOpen) {
            setSelectedCampaign(null);
        }
    };
    
    const showCampaignDetails = (campaign) => {
        setSelectedCampaign(campaign);
        setIsModalOpen(true);
    };
    
    const formatDate = (dateString) => {
        if (!dateString) return "Not set";
        return new Date(dateString).toLocaleString();
    };
    
    const formatTimeRanges = (timeRanges) => {
        if (!timeRanges || timeRanges.length === 0) return "No time restrictions";
        
        return timeRanges.map((range, index) => (
            <div key={index} className="mb-1">
                <span className="fw-medium">{range.day}: </span>
                {range.startTime} - {range.endTime}
            </div>
        ));
    };
    
    const formatEventConfig = (config, eventType) => {
        if (!config) return "No configuration";
        
        if (eventType === "IN_APP_EVENT") {
            return (
                <div>
                    <span className="fw-medium">Delay: </span>
                    {config.delayMs ? `${config.delayMs} ms` : "No delay"}
                </div>
            );
        }
        
        if (eventType === "REAL_TIME_EVENT") {
            return (
                <div>
                    {config.profiles && config.profiles.length > 0 && (
                        <div className="mb-2">
                            <span className="fw-medium">Profiles: </span>
                            <div className="mt-1">
                                {config.profiles.map((profile, index) => (
                                    <Badge key={index} color="primary" className="me-1 mb-1">
                                        {profile.replace(/_/g, " ")}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {config.genderTypes && config.genderTypes.length > 0 && (
                        <div className="mb-2">
                            <span className="fw-medium">Gender: </span>
                            <div className="mt-1">
                                {config.genderTypes.map((gender, index) => (
                                    <Badge key={index} color="info" className="me-1 mb-1">
                                        {gender}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {config.generationTypes && config.generationTypes.length > 0 && (
                        <div className="mb-2">
                            <span className="fw-medium">Generation: </span>
                            <div className="mt-1">
                                {config.generationTypes.map((gen, index) => (
                                    <Badge key={index} color="secondary" className="me-1 mb-1">
                                        {gen}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            );
        }
        
        return JSON.stringify(config, null, 2);
    };
    
    const formatOffer = (offer) => {
        if (!offer) return "No offer configuration";
        
        return (
            <div>
                <div className="mb-2">
                    <span className="fw-medium">Type: </span>
                    {offer.type}
                </div>
                
                <div className="mb-2">
                    <span className="fw-medium">Offer Type: </span>
                    {offer.offerType}
                </div>
                
                <div className="mb-2">
                    <span className="fw-medium">Title: </span>
                    {offer.title}
                </div>
                
                <div className="mb-2">
                    <span className="fw-medium">Body: </span>
                    {offer.body}
                </div>
                
                <div className="mb-2">
                    <span className="fw-medium">URL: </span>
                    {offer.redirectUrl}
                </div>
                
                {offer.imageUrl && (
                    <div className="mb-2">
                        <span className="fw-medium">Image: </span>
                        <div className="mt-2">
                            <img 
                                src={offer.imageUrl} 
                                alt="Notification image" 
                                style={{ maxWidth: "100%", maxHeight: "200px", objectFit: "contain" }}
                                onError={(e) => { e.target.src = "https://via.placeholder.com/200x100?text=Image+Not+Available"; }}
                            />
                        </div>
                    </div>
                )}
                
                <div className="mb-2">
                    <span className="fw-medium">Cancel Button: </span>
                    {offer.disableCancel ? "Disabled" : "Enabled"}
                </div>
            </div>
        );
    };

    document.title = "AIMEE - Campaign Management";
    
    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Campaign Management" pageTitle="Sales Growth" />
                    
                    {actionMessage && (
                        <Alert color={actionMessage.type} className="mt-2">
                            {actionMessage.text}
                        </Alert>
                    )}
                    
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="d-flex justify-content-between align-items-center">
                                    <h4 className="card-title mb-0">Campaign List</h4>
                                    <div className="d-flex gap-2">
                                        <div className="search-box me-2">
                                            <input
                                                type="text"
                                                className="form-control search"
                                                placeholder="Search campaigns..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                            <i className="ri-search-line search-icon"></i>
                                        </div>
                                        <Link to="/sales-growth/current" className="btn btn-primary">
                                            <FaPlus className="me-1" /> Create New Campaign
                                        </Link>
                                    </div>
                                </CardHeader>
                                <CardBody>
                                    {loading || campaignActions.loading ? (
                                        <div className="text-center my-3">
                                            <Spinner color="primary" />
                                            <p className="mt-2">
                                                {loading ? "Loading campaigns..." : "Processing operation..."}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <Table className="table-striped table-nowrap align-middle mb-0">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">Campaign Name</th>
                                                        <th scope="col" className="text-center">Status</th>
                                                        <th scope="col" className="text-center">Type</th>
                                                        <th scope="col" className="text-center">Notification Type</th>
                                                        <th scope="col" className="text-center">Target Audience</th>
                                                        <th scope="col">Start Date</th>
                                                        <th scope="col">End Date</th>
                                                        <th scope="col">Daily Quota</th>
                                                        <th scope="col">Total Quota</th>
                                                        <th scope="col" className="text-center">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredCampaigns.length > 0 ? (
                                                        filteredCampaigns.map((campaign) => (
                                                            <tr key={campaign.id}>
                                                                <td>
                                                                    <div>
                                                                        <span className="fw-medium">{campaign.name}</span>
                                                                        {campaign.description && (
                                                                            <div className="text-muted small">{campaign.description}</div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="text-center">{getStatusBadge(campaign.status)}</td>
                                                                <td className="text-center">{getCampaignType(campaign.eventType)}</td>
                                                                <td className="text-center">{getNotificationType(campaign.offer)}</td>
                                                                <td className="text-center">{getAudienceInfo(campaign)}</td>
                                                                <td>{new Date(campaign.startDate).toLocaleDateString()}</td>
                                                                <td>{new Date(campaign.endDate).toLocaleDateString()}</td>
                                                                <td>{campaign.dailyQuota.toLocaleString()}</td>
                                                                <td>{campaign.totalQuota.toLocaleString()}</td>
                                                                <td className="text-center">
                                                                    <div className="d-flex justify-content-center gap-2">
                                                                        <Button
                                                                            color="light"
                                                                            size="sm"
                                                                            className="text-primary"
                                                                            title="View Details"
                                                                            onClick={() => showCampaignDetails(campaign)}
                                                                        >
                                                                            <FaEye />
                                                                        </Button>
                                                                        {campaign.status === "IN_PROGRESS" && (
                                                                            <Button
                                                                                color="light"
                                                                                size="sm"
                                                                                className="text-warning"
                                                                                onClick={() => handleStatusChange(campaign.id, "IN_PROGRESS")}
                                                                                title="Disable"
                                                                            >
                                                                                <FaPause />
                                                                            </Button>
                                                                        )}
                                                                        {campaign.status === "DISABLED" && (
                                                                            <Button
                                                                                color="light"
                                                                                size="sm"
                                                                                className="text-success"
                                                                                onClick={() => handleStatusChange(campaign.id, "DISABLED")}
                                                                                title="Activate"
                                                                            >
                                                                                <FaPlay />
                                                                            </Button>
                                                                        )}
                                                                        {(campaign.status === "AUDIENCE_PROCESSING" || campaign.status === "AUDIENCE_FAILED") && (
                                                                            <Button
                                                                                color="light"
                                                                                size="sm"
                                                                                className="text-info"
                                                                                title="Audience Processing"
                                                                                disabled
                                                                            >
                                                                                <i className="ri-refresh-line"></i>
                                                                            </Button>
                                                                        )}
                                                                        <Button
                                                                            color="light"
                                                                            size="sm"
                                                                            className="text-danger"
                                                                            title="Delete"
                                                                            onClick={() => handleDeleteCampaign(campaign.id, campaign.name)}
                                                                        >
                                                                            <FaTrash />
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="10" className="text-center">
                                                                {error ? 
                                                                    <div className="text-danger">Error: Failed to load campaign data</div> : 
                                                                    "No campaigns found"}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    
                    {/* Campaign Details Modal */}
                    <Modal isOpen={isModalOpen} toggle={toggleModal} size="xl" className="campaign-details-modal" style={{ maxWidth: '95%', width: '95%', margin: '10px auto' }}>
                        <ModalHeader toggle={toggleModal} className="bg-light border-bottom">
                            {selectedCampaign ? `Campaign Details: ${selectedCampaign.name}` : "Campaign Details"}
                        </ModalHeader>
                        <ModalBody className="p-3">
                            {selectedCampaign ? (
                                <div className="campaign-details">
                                    <Row className="g-3 mb-3">
                                        <Col md={4}>
                                            <div className="border rounded p-3 h-100">
                                                <h5 className="text-primary mb-3 border-bottom pb-2">General Information</h5>
                                                <div className="mb-2">
                                                    <span className="fw-medium">ID: </span>
                                                    {selectedCampaign.id}
                                                </div>
                                                <div className="mb-2">
                                                    <span className="fw-medium">Name: </span>
                                                    {selectedCampaign.name}
                                                </div>
                                                <div className="mb-2">
                                                    <span className="fw-medium">Description: </span>
                                                    {selectedCampaign.description || "No description"}
                                                </div>
                                                <div className="mb-2">
                                                    <span className="fw-medium">Status: </span>
                                                    {getStatusBadge(selectedCampaign.status)}
                                                </div>
                                                <div className="mb-2">
                                                    <span className="fw-medium">Type: </span>
                                                    {getCampaignType(selectedCampaign.eventType)}
                                                </div>
                                                <div className="mb-2">
                                                    <span className="fw-medium">License Key: </span>
                                                    <span className="text-muted font-size-12">{selectedCampaign.licenseKey}</span>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="border rounded p-3 h-100">
                                                <h5 className="text-primary mb-3 border-bottom pb-2">Quotas & Schedule</h5>
                                                <Row>
                                                    <Col md={6}>
                                                        <div className="mb-2">
                                                            <span className="fw-medium">Daily Quota: </span>
                                                            {selectedCampaign.dailyQuota.toLocaleString()}
                                                        </div>
                                                        <div className="mb-2">
                                                            <span className="fw-medium">Total Quota: </span>
                                                            {selectedCampaign.totalQuota.toLocaleString()}
                                                        </div>
                                                        <div className="mb-2">
                                                            <span className="fw-medium">Campaign Timer: </span>
                                                            {selectedCampaign.specificCampaignTimerHour || "Not set"} hours
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="mb-2">
                                                            <span className="fw-medium">Start Date: </span>
                                                            {formatDate(selectedCampaign.startDate)}
                                                        </div>
                                                        <div className="mb-2">
                                                            <span className="fw-medium">End Date: </span>
                                                            {formatDate(selectedCampaign.endDate)}
                                                        </div>
                                                        <div className="mb-2">
                                                            <span className="fw-medium">Audience Count: </span>
                                                            {selectedCampaign.audienceCount ? selectedCampaign.audienceCount.toLocaleString() : "0"}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="border rounded p-3 h-100">
                                                <h5 className="text-primary mb-3 border-bottom pb-2">Time Restrictions</h5>
                                                {formatTimeRanges(selectedCampaign.timeRanges)}
                                            </div>
                                        </Col>
                                    </Row>
                                    
                                    <Row className="g-3">
                                        <Col md={4}>
                                            <div className="border rounded p-3 h-100">
                                                <h5 className="text-primary mb-3 border-bottom pb-2">Target Audience</h5>
                                                <div className="mb-2">
                                                    <span className="fw-medium">Audience ID: </span>
                                                    {selectedCampaign.audienceId || "Dynamic audience"}
                                                </div>
                                                {selectedCampaign.eventType === "REAL_TIME_EVENT" && selectedCampaign.eventConfig && (
                                                    <div className="mt-3">
                                                        <h6 className="border-bottom pb-1 mb-2">Event Configuration:</h6>
                                                        {formatEventConfig(selectedCampaign.eventConfig, selectedCampaign.eventType)}
                                                    </div>
                                                )}
                                                {selectedCampaign.eventType === "IN_APP_EVENT" && selectedCampaign.eventConfig && (
                                                    <div className="mt-3">
                                                        <h6 className="border-bottom pb-1 mb-2">Event Configuration:</h6>
                                                        {formatEventConfig(selectedCampaign.eventConfig, selectedCampaign.eventType)}
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                        
                                        <Col md={8}>
                                            <div className="border rounded p-3 h-100">
                                                <h5 className="text-primary mb-3 border-bottom pb-2">Notification Details</h5>
                                                {selectedCampaign.offer ? (
                                                    <div className="offer-details">
                                                        <Row>
                                                            <Col md={7}>
                                                                <Row>
                                                                    <Col md={6}>
                                                                        <div className="mb-3">
                                                                            <span className="fw-medium">Type: </span>
                                                                            <Badge color="info" className="ms-1">
                                                                                {selectedCampaign.offer.type}
                                                                            </Badge>
                                                                        </div>
                                                                        
                                                                        <div className="mb-3">
                                                                            <span className="fw-medium">Offer Type: </span>
                                                                            <Badge color="secondary" className="ms-1">
                                                                                {selectedCampaign.offer.offerType}
                                                                            </Badge>
                                                                        </div>
                                                                        
                                                                        <div className="mb-3">
                                                                            <span className="fw-medium">Cancel Button: </span>
                                                                            <Badge color={selectedCampaign.offer.disableCancel ? "danger" : "success"} className="ms-1">
                                                                                {selectedCampaign.offer.disableCancel ? "Disabled" : "Enabled"}
                                                                            </Badge>
                                                                        </div>
                                                                    </Col>
                                                                    
                                                                    <Col md={6}>
                                                                        <div className="mb-3">
                                                                            <span className="fw-medium">URL: </span>
                                                                            <div className="mt-1 p-2 bg-light rounded">
                                                                                <a href={selectedCampaign.offer.redirectUrl} target="_blank" rel="noopener noreferrer" className="text-break">
                                                                                    {selectedCampaign.offer.redirectUrl}
                                                                                </a>
                                                                            </div>
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                                
                                                                <div className="mb-3">
                                                                    <span className="fw-medium">Title: </span>
                                                                    <div className="mt-1 p-2 bg-light rounded">
                                                                        {selectedCampaign.offer.title}
                                                                    </div>
                                                                </div>
                                                                
                                                                <div className="mb-3">
                                                                    <span className="fw-medium">Body: </span>
                                                                    <div className="mt-1 p-2 bg-light rounded">
                                                                        {selectedCampaign.offer.body}
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                            
                                                            <Col md={5}>
                                                                {selectedCampaign.offer.imageUrl && (
                                                                    <div className="text-center border rounded p-3 h-100 d-flex flex-column justify-content-center">
                                                                        <span className="fw-medium d-block mb-2">Notification Image</span>
                                                                        <img 
                                                                            src={selectedCampaign.offer.imageUrl} 
                                                                            alt="Notification image" 
                                                                            className="img-thumbnail"
                                                                            style={{ maxWidth: "100%", maxHeight: "200px", objectFit: "contain" }}
                                                                            onError={(e) => { e.target.src = "https://via.placeholder.com/300x200?text=Image+Not+Available"; }}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                ) : (
                                                    <div className="text-muted">No notification details available</div>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <Spinner color="primary" />
                                    <p className="mt-2">Loading campaign details...</p>
                                </div>
                            )}
                        </ModalBody>
                        <ModalFooter className="bg-light border-top">
                            <Button color="primary" onClick={toggleModal}>Close</Button>
                        </ModalFooter>
                    </Modal>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default CampaignManagement; 